import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ViewContainerRef,
  Injector,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from 'src/app/common/base/base.component';
import { CatalogService } from '../../catalog-service';
import { Observable, Subscription } from 'rxjs';
import {
  Roles,
  Catalog,
  ActionPendingCatalog,
  PendingCatalogColor,
  StatusPendingCatalog,
} from 'src/app/common/enumerations/enumerations';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { utils } from 'src/app/modules/libs/utils';
import { Catalogs } from '../../catalog-model';
import {
  PendingCatalog,
  PendingCatalogType,
  PendingRole,
} from 'src/app/services/pending-approvals/pending-approval';

@Component({
  selector: 'app-roles-list',
  templateUrl: './roles-list.component.html',
  styleUrls: ['./roles-list.component.scss'],
})
export class RolesListComponent
  extends BaseComponent
  implements OnInit, OnChanges, OnDestroy {
  @Output() rowSelected = new EventEmitter<any>();

  roles!: Role[];
  rolesfiltered!: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;
  roleCreatedCode?: string | null;
  filter!: string;

  constructor(
    protected override injector: Injector,
    private _catalogService: CatalogService,
    private route: ActivatedRoute,
  ) {
    super(injector);
  }
  public displayedColumnsRoles: string[] = [
    'role',
    'name',
    'category',
    'group',
  ];
  public dataSource!: MatTableDataSource<Role>;

  public selectedRowIndex?: number;
  public roleIndex!: number;

  public createAvailable?: boolean;
  public showInactive!: boolean;
  public pending = false;
  rowHighlighted: any;
  paramRoleID?: number;

  @ViewChildren('matrow', { read: ViewContainerRef }) rows!: QueryList<ViewContainerRef>;
  @ViewChild(MatSort) sort!: MatSort;

  override ngOnDestroy(): void {
    this.rolesSubs?.unsubscribe();
  }

  ngOnInit() {
    this.clear();
    this.refreshRoles();

    this.route.params.subscribe((params) => {
      const roleID = +params['roleID'];
      if (roleID) {
        this.paramRoleID = roleID;
        this.loadRoles();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.createAvailable = this.currentUser?.userRole?.some(r => r.roleID === Roles.ADM || r.roleID == Roles.PC);
  }

  refreshRoles(event?: any) {
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource();
      this.setSortingAccessor();
    }
    this.loadRoles();
  }

  selectParam(id: number) {
    const role = this.roles?.find((x) => x.id === id);
    if (role) {
      this.rowHighlighted = null;
      this.showIndex(role.id);
    }
  }

  loadRoles() {
    this.clear();
    const pendingRole =
      this._catalogService.pendingCatalog?.approvalType ==
        PendingCatalogType.Role
        ? this._catalogService.pendingCatalog
        : null;
    if (pendingRole) this.showInactive = true;
    this.rolesSubs = this.store.select((state) => state.Roles.data).subscribe((data) => {
      if (!data?.length) {
        data.roles = [];
      }

      this.roles = data.filter((r: Role) =>
        this.showInactive ||
        r.status == 1 ||
        this.isPendingRole(r)
      ) as Role[];

      this.applyFilter();
      this.roles = this.roles.sort((a, b) => this.utils.sort(a.code, b.code));
      this.dataSource = new MatTableDataSource(this.roles);
      this.setSortingAccessor();

      if (pendingRole && pendingRole.id > 0) {
        this.showIndex(pendingRole.id);
      } else if (pendingRole && !pendingRole.id) {
        this.showIndex(pendingRole.pendingID * -1);
      } else if (this.roleCreatedCode) {
        const row = this.dataSource.data.find(
          (x) => x.code == this.roleCreatedCode,
        );
        this.getRoleInfo(row);
        this.roleCreatedCode = null;
      } else if (this.paramRoleID) {
        this.selectParam(this.paramRoleID);
        this.paramRoleID = 0;
      }
    });
  }

  setSortingAccessor() {
    // Define the sorting accessor
    this.dataSource.sortingDataAccessor = (element, property) => {
      switch (property) {
        case 'role':
          return element.code;
        case 'category':
          return element?.aplGroupRole?.aplGroup?.aplGroupMaster
            ?.aplCategoryMaster?.aplCategory?.code;
        case 'group':
          return element?.aplGroupRole?.aplGroup?.name;
        default:
          return (element as any)[property]; // Fallback to the default property
      }
    };
    this.setSort<Role>(this.dataSource, this.sort);
  }

  applyFilter(filterValue?: string) {
    this.filter = filterValue?.toLowerCase() ?? '';
    this.dataSource = new MatTableDataSource(
      this.roles.filter(
        (r) =>
          r.aplGroupRole?.aplGroup?.aplGroupMaster?.aplCategoryMaster?.aplCategory?.code
            ?.toLowerCase()
            .includes(this.filter) ||
          r.name?.toLowerCase().includes(this.filter) ||
          r.code?.toLowerCase().includes(this.filter) ||
          r.aplGroupRole?.aplGroup?.name?.toLowerCase().includes(this.filter),
      ),
    );
    this.setSortingAccessor();
  }

  showRolesInactive() {
    this.refreshRoles();
  }

  isPendingRole(role?: Role | null): boolean {
    const color = role?.color as any;
    const hasPendingRole = role?.pendingRoles?.some(
      (pendingRole) =>
        pendingRole?.status === StatusPendingCatalog.Pending ||
        (pendingRole?.status as any) === 'Pending',
    );

    return !!(
      hasPendingRole ||
      color === PendingCatalogColor.Modify ||
      color === PendingCatalogColor.Create ||
      color === PendingCatalogColor.ModifyRSS ||
      color === 'Modify' ||
      color === 'Create' ||
      color === 'ModifyRSS'
    );
  }

  getRoleStatusClasses(role?: Role | null) {
    const pendingDelete = this.isPendingDeleteRole(role);
    const pending = this.isPendingRole(role);
    return {
      green: role?.status == 1 && !pending && !pendingDelete,
      gray: role?.status == 0 && !pending && !pendingDelete,
      yellow: pending && !pendingDelete,
      red: pendingDelete,
    };
  }

  isPendingDeleteRole(role?: Role | null): boolean {
    const action = role?.pendingRoles?.at(-1)?.action as any;
    return (
      action === ActionPendingCatalog.Delete ||
      action === 'Delete'
    );
  }

  roleSelected(role: Role | null) {
    if (role) {
      this.roleCreatedCode = role.code;
      this._catalogService.pendingCatalog = undefined;
      this.pending = false;
      this.refreshRoles();
      this.rowHighlighted = null;
    } else {
      this.rowHighlighted = null;
    }
    this.rowSelected.emit(this.rowHighlighted);
  }

  async getRoleInfo(row?: Role) {
    if (!(await this.canDeactivate())) return;

    if (this.rowHighlighted?.id == row?.id && !this.roleCreatedCode) {
      this.rowHighlighted = null;
      this.selectedRowIndex = -1;
      this._catalogService.pendingCatalog = undefined;
      this.rowSelected.emit(null);
    } else {
      const role = utils.cloneDeep(row);
      this.rowHighlighted = role;
      this._catalogService.pendingCatalog = {
        id: role?.id,
      } as PendingCatalog;
      this.rowSelected.emit(this.rowHighlighted);
    }
  }

  setOriginalRole(id: number) {
    this.selectParam(id);
  }

  async createRole() {
    if (await this.canDeactivate()) {
      this.selectedRowIndex = 0;
      const role = {
        id: 0,
        status: 1,
        color: PendingCatalogColor.Create,
      } as Role;
      this.rowSelected.emit(role);
    }
  }

  clear() {
    this.roles = [];
  }

  showIndex(id: number) {
    this.roleIndex = id;
    this.selectedRowIndex = this.dataSource.data.findIndex((x) => x.id == id);
    if (this.selectedRowIndex == -1) {
      this.selectedRowIndex = this.dataSource.data.findIndex(
        (x) => x.id == -id,
      );
    }
    const roleSelected = this.dataSource.data[this.selectedRowIndex];
    this.showElement(this.selectedRowIndex, 600);
    if (roleSelected) {
      this.getRoleInfo(roleSelected);
    }
  }

  showElement(index: number, height: number) {
    const that = this;
    setTimeout(() => {
      const indexstr = index.toString();
      const row = that.rows.find(
        (r) => r.element.nativeElement.getAttribute('position') === indexstr,
      );
      if (row) {
        const rect = row.element.nativeElement.getBoundingClientRect();
        if (rect.y <= 0 || rect.y + rect.height > height) {
          row.element.nativeElement.scrollIntoView(false, {
            behavior: 'instant',
          });
        }
      }
    }, 300);
  }

  getPosition(row: any): number {
    const i = this.dataSource.data.findIndex((r) => {
      return r.id == row.id;
    });
    return i;
  }
}
